import React from 'react';
import { Form, Formik } from 'formik';
import { Checkbox, Divider, FormControlLabel, FormGroup, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { KeyedMutator } from 'swr/_internal';
import { LoadingButton } from '@mui/lab';
import { AccountData, AccountService, PartialLabelData, PassOrderPageData } from '../api';
import { FormRichTextField } from '../experienceSettings/formComponents/FormRichTextField';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { getPassOrderPages } from '../utils/AccountUtils';
import { useLanguage } from '../common/GeneralUtils';

type AccountPassSettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

type FormValues = {
  guest_pass_waiver_text: PartialLabelData;
  hidden_pass_order_page_ids: string[];
};

export const AccountPassSettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: AccountPassSettingsProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const passOrderPages = getPassOrderPages(account);

  const initialValues: FormValues = {
    guest_pass_waiver_text: account?.guest_pass_waiver_text || {},
    hidden_pass_order_page_ids: account?.pass_customer_portal_settings?.hidden_pass_order_page_ids || [],
  };

  const validationSchema = yup.object().shape({
    guest_pass_waiver_text: yup.object().shape({
      en: yup.string(),
      he: yup.string(),
    }),
    hidden_pass_order_page_ids: yup.array().of(yup.string()),
  });

  const onSubmit = async (
    values: FormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    try {
      setSubmitting(true);
      await AccountService.updateGuestPassSettings({ guest_pass_waiver_text: values.guest_pass_waiver_text });
      const updatedPortalSettings = await AccountService.updatePassCustomerPortalSettings({
        hidden_pass_order_page_ids: values.hidden_pass_order_page_ids,
      });
      await updateLocalAccount({
        ...account,
        guest_pass_waiver_text: values.guest_pass_waiver_text,
        pass_customer_portal_settings: updatedPortalSettings,
      });
      setIsUpdatedSnackOpen(true);
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ isSubmitting, dirty, values, setFieldValue }) => {
        const formIsDisabled = readOnly || isSubmitting;
        const toggleHiddenPass = (passId: string, checked: boolean) => {
          const next = checked
            ? Array.from(new Set([...values.hidden_pass_order_page_ids, passId]))
            : values.hidden_pass_order_page_ids.filter((id) => id !== passId);
          void setFieldValue('hidden_pass_order_page_ids', next);
        };
        return (
          <Form>
            <Grid container flexDirection="column">
              <Grid container flexDirection="column" gap={2}>
                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h4">{t('accountSettings.passSettingsTitle')}</Typography>
                </Grid>
                <Grid item mb={2}>
                  <Typography variant="body1" color="text.secondary">
                    {t('accountSettings.passSettingsDescription')}
                  </Typography>
                </Grid>

                <Divider />

                <Grid item mt={2}>
                  <Typography variant="h6">{t('accountSettings.guestPassWaiverTitle')}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    {t('accountSettings.guestPassWaiverDescription')}
                  </Typography>
                </Grid>
                <FormRichTextField labelFieldName="guest_pass_waiver_text" disabled={formIsDisabled} />
                <Grid item mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('accountSettings.guestPassWaiverSnapshotNote')}
                  </Typography>
                </Grid>

                {passOrderPages.length > 0 && (
                  <>
                    <Grid item mt={3}>
                      <Divider />
                    </Grid>
                    <Grid item mt={2}>
                      <Typography variant="h6">{t('accountSettings.passCustomerPortalVisibilityTitle')}</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body2" color="text.secondary">
                        {t('accountSettings.passCustomerPortalVisibilityDescription')}
                      </Typography>
                    </Grid>
                    <Grid item mt={1}>
                      <Typography fontWeight={700}>{t('accountSettings.passCustomerPortalHiddenListTitle')}</Typography>
                    </Grid>
                    <Grid item>
                      <FormGroup>
                        {passOrderPages.map((page: PassOrderPageData) => (
                          <FormControlLabel
                            key={page.id}
                            control={
                              <Checkbox
                                checked={values.hidden_pass_order_page_ids.includes(page.id)}
                                disabled={formIsDisabled}
                                onChange={(_, checked) => toggleHiddenPass(page.id, checked)}
                              />
                            }
                            label={<Typography>{page.label[language] || page.id}</Typography>}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid container justifyContent="flex-end" mt={3}>
                <LoadingButton
                  loading={isSubmitting}
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formIsDisabled || !dirty}>
                  {t('save')}
                </LoadingButton>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
